import './MetaConnection.scss';

import { Alert, Badge, Button, Card, Container, Spinner } from 'react-bootstrap';
import { FaCheckCircle, FaExclamationTriangle, FaFacebook, FaInstagram } from 'react-icons/fa';
import React, { useEffect, useState } from 'react';

import FloatingVideoButton from '../../commons/buttons/FloatingVideoButton';
import MetaAPI from './MetaAPI';
import { useNavigate } from 'react-router-dom';

const metaAPI = new MetaAPI();

const MetaConnection = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connectedPages, setConnectedPages] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Verifica se já existe conexão ao carregar a página
  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      setLoading(true);
      // Aqui você faria uma chamada ao seu backend para verificar se já existe token salvo
      const response = await metaAPI.checkConnection();
      const data = await response.data;

      if (data.connected) {
        setIsConnected(true);
        setConnectedPages(data.pages || []);
      } else {
        setIsConnected(false);
        setConnectedPages([]);
      }
    } catch (err) {
      setIsConnected(false);
      setConnectedPages([]);
      console.error('Erro ao verificar conexão:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectMeta = () => {
    setLoading(true);
    setError(null);

    // Configurações do OAuth da Meta
    const APP_ID = '1569142691194026'; // Substitua pelo ID do seu app
    const REDIRECT_URI = encodeURIComponent('https://gestao.hallapp.com.br/meta/callback');
    const SCOPE = encodeURIComponent('email,pages_show_list,pages_manage_ads,ads_management,business_management,instagram_basic,ads_read,ads_management,read_insights');

    // URL de autenticação da Meta
    const authUrl = `https://www.facebook.com/v24.0/dialog/oauth?client_id=${APP_ID}&redirect_uri=${REDIRECT_URI}&scope=${SCOPE}&response_type=code`;

    // Redireciona para a página de autorização da Meta
    window.location.href = authUrl;
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar sua conta Meta? Você não poderá mais criar anúncios.')) {
      return;
    }

    try {
      setLoading(true);
      await metaAPI.disconnect()
      await checkConnection();
    } catch (err) {
      setError('Erro ao desconectar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="meta-connection-page py-4">
      <div className="page-header mb-4">
        <h2>Conectar com Meta (Facebook & Instagram)</h2>
        <p className="text-muted">
          Conecte sua página do Facebook e Instagram para criar e gerenciar anúncios diretamente pelo Hallapp.
        </p>
        <div className="alert alert-info mt-3">
          Para conectar sua conta Meta, é necessário que você já tenha uma
          <strong> conta de anúncios configurada</strong>.
          Isso inclui <strong>confirmar a forma de pagamento </strong> e
          <strong> adicionar um cartão de crédito</strong> na sua conta de anúncios.
          <br /><br />
          Caso ainda não tenha configurado, veja o passo a passo neste vídeo:
          <a
            href="https://www.youtube.com/watch?v=7itluwCjpZc"
            target="_blank"
            rel="noopener noreferrer"
          >
            👉 Como criar conta de anúncio e cadastrar o cartão (YouTube)
          </a>
          
          <br></br><br></br>
          <b>Abaixo o link do Facebook Business para configurar a forma de pagamento</b>
          <a href='https://business.facebook.com/latest/settings/' target='_blank'> 👉 Acesse o facebook business</a>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <FaExclamationTriangle className="me-2" />
          {error}
        </Alert>
      )}

      <Card className="connection-card shadow-sm">
        <Card.Body className="p-4">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Carregando...</p>
            </div>
          ) : !isConnected ? (
            <div className="not-connected text-center">
              <div className="icons-group mb-4">
                <FaFacebook size={60} className="text-primary me-3" />
                <FaInstagram size={60} className="text-danger" />
              </div>

              <h4 className="mb-3">Conecte sua conta Meta</h4>

              <p className="text-muted mb-4">
                Ao conectar, você poderá:
              </p>

              <ul className="benefits-list text-start mb-4">
                <li>✅ Criar anúncios no Facebook e Instagram</li>
                <li>✅ Gerenciar campanhas ativas</li>
                <li>✅ Acompanhar resultados em tempo real</li>
                <li>✅ Promover serviços e horários disponíveis</li>
                <li>✅ Atrair novos clientes para o salão</li>
              </ul>

              <Button
                variant="primary"
                size="lg"
                onClick={handleConnectMeta}
                className="connect-button"
              >
                <FaFacebook className="me-2" />
                Conectar com Meta
              </Button>

              <p className="text-muted small mt-3">
                Você será redirecionado para o Facebook para autorizar o acesso.
              </p>
            </div>
          ) : (
            <div className="connected">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center">
                  <FaCheckCircle size={32} className="text-success me-3" />
                  <div>
                    <h5 className="mb-0">Conta conectada com sucesso!</h5>
                    <p className="text-muted small mb-0">Você já pode criar anúncios</p>
                  </div>
                </div>
                <Badge bg="success" className="px-3 py-2">Ativo</Badge>
              </div>

              {connectedPages.length > 0 && (
                <div className="connected-pages mb-4">
                  <h6 className="mb-3">Páginas conectadas:</h6>
                  {connectedPages.map((page) => (
                    <Card key={page.id} className="page-item mb-2">
                      <Card.Body className="d-flex align-items-center p-3">
                        <div className="page-icon me-3">
                          {page.picture ? (
                            <img
                              src={page.picture}
                              alt={page.name}
                              className="rounded-circle"
                              width="40"
                              height="40"
                            />
                          ) : (
                            <div className="placeholder-icon rounded-circle bg-secondary d-flex align-items-center justify-content-center">
                              <FaFacebook className="text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex-grow-1">
                          <strong>{page.name}</strong>
                          <div className="small text-muted">ID: {page.id}</div>
                        </div>
                        <Badge bg="primary">Conectada</Badge>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}

              <div className="d-flex gap-2">
                <Button
                  variant="outline-danger"
                  onClick={handleDisconnect}
                >
                  Desconectar
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/marketing/criar-anuncio')}
                >
                  Criar Anúncio
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
      <FloatingVideoButton youtubeUrl={"https://www.youtube.com/embed/dQGOxrKkELQ"} />

      <Card className="mt-4 border-0 bg-light">
        <Card.Body>
          <h6 className="mb-3">ℹ️ Informações importantes:</h6>
          <ul className="small text-muted mb-0">
            <li>Você precisa ser administrador da página do Facebook para conectá-la.</li>
            <li>A conexão é segura e utiliza o protocolo OAuth 2.0 da Meta.</li>
            <li>Você pode desconectar a qualquer momento.</li>
            <li>O Hallapp nunca terá acesso à sua senha do Facebook.</li>
          </ul>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MetaConnection;